import { Button, Loader, Modal, Title } from "@mantine/core";
import { useState } from "react";
import { useContainerRef } from "../../../hooks/useContainerRef";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { SubMeteringSystem } from "../../../types/api.types";
import type { Site } from "../../../utils/backend-types";
import type { SelectedMeter } from "../EditConfigurationModal/EditConfigurationModal";
import { IMPORT_RUNNING_TEXT } from "../HistoricalDataImport/HistoricalDataImport";
import { HistoricalDataImportWithStateAndControls } from "../HistoricalDataImport/HistoricalDataImportWithStateAndControls";
import { useSubMeteringConfigurationMutations } from "../hooks/useSubMeteringConfigurationMutations";
import { MeterSelection } from "../MeterSelection/MeterSelection";
import {
  SUB_METERING_FORM_ID,
  SubMeteringConfigurationForm
} from "../SubMeteringConfigurationForm/SubMeteringConfigurationForm";
import type { ConfigurationData } from "../ThirdPartySystems.types";
import { getSystemDisplayNameFromValue } from "../utils/getSystemDisplayNameFromValue";

const STEPS = {
  CONFIGURATION_FORM: 1,
  METER_SELECTION: 2,
  HISTORICAL_DATA_IMPORT: 3,
  SUMMARY: 4
};

interface NewConfigurationModalProps {
  isOpen: boolean;
  sites: Array<Site>;
  variantId: number;
  onConfigurationCreated: (configuration: ConfigurationData) => void;
  onToggle: () => void;
}

function NewConfigurationModal({
  isOpen,
  sites,
  variantId,
  onConfigurationCreated,
  onToggle
}: NewConfigurationModalProps) {
  const [step, setStep] = useState(STEPS.CONFIGURATION_FORM);
  const [configurationId, setConfigurationId] = useState<number | null>(null);
  const [systemName, setSystemName] = useState<SubMeteringSystem | undefined>(
    undefined
  );
  const [showImportRunningMessage, setShowImportRunningMessage] =
    useState(false);
  const [
    isSelectedMetersWarningModalOpen,
    setIsSelectedMetersWarningModalOpen
  ] = useState(false);
  const [selectedMeters, setSelectedMeters] = useState<Array<SelectedMeter>>(
    []
  );

  const { containerNode, containerRef } = useContainerRef();
  const { showErrorToast } = useToast();
  const {
    createSubMeteringConfigurationMutation,
    triggerHistoricalDataImportMutation
  } = useSubMeteringConfigurationMutations(variantId);

  async function handleSubmitConfigurationForm(data: ConfigurationData) {
    const responseData = (
      await createSubMeteringConfigurationMutation.mutateAsync(data)
    ).data;

    setSystemName(responseData.subMeteringSystem);
    setConfigurationId(responseData.id);
    setStep(STEPS.METER_SELECTION);

    onConfigurationCreated({
      ...responseData,
      meters: []
    });

    return responseData;
  }

  function handleNextStep() {
    setStep(STEPS.HISTORICAL_DATA_IMPORT);
  }

  async function handleSubmitHistoricalDateImportDates(
    startDate: string,
    endDate: string
  ) {
    if (!configurationId) {
      return Promise.reject();
    }

    try {
      await triggerHistoricalDataImportMutation.mutateAsync({
        configurationId,
        endDate,
        startDate
      });
      setShowImportRunningMessage(true);
      setStep(STEPS.SUMMARY);
    } catch (error) {
      showErrorToast(error);
      return await Promise.reject();
    }
  }

  function handleSkipHistoricalDateImport() {
    setShowImportRunningMessage(false);
    setStep(STEPS.SUMMARY);
  }

  function handleModalClose() {
    if (selectedMeters.length > 0) {
      setIsSelectedMetersWarningModalOpen(true);
      return;
    }

    onToggle();
  }

  return (
    <Modal
      className="NewConfigurationModal"
      opened={isOpen}
      size={step === STEPS.METER_SELECTION ? "min(80vw, 1200px)" : undefined}
      title="Neue Konfiguration einrichten"
      onClose={handleModalClose}
    >
      {step === STEPS.CONFIGURATION_FORM && (
        <div>
          <Title order={5}>Verbindung</Title>
          <SubMeteringConfigurationForm
            variantId={variantId}
            onSubmit={handleSubmitConfigurationForm}
          />
        </div>
      )}
      {step === STEPS.METER_SELECTION && (
        <div>
          {systemName && configurationId ? (
            <MeterSelection
              buttonContainer={containerNode}
              configurationId={configurationId}
              isSelectedMetersWarningModalOpen={
                isSelectedMetersWarningModalOpen
              }
              selectedMeters={selectedMeters}
              setIsSelectedMetersWarningModalOpen={
                setIsSelectedMetersWarningModalOpen
              }
              setSelectedMeters={setSelectedMeters}
              sites={sites}
              systemName={systemName}
              onNextStep={handleNextStep}
              onToggle={onToggle}
            />
          ) : (
            <Loader />
          )}
        </div>
      )}
      {step === STEPS.HISTORICAL_DATA_IMPORT && (
        <div>
          <Title order={5}>
            Import von historischen Daten aus Untermesssystemen
          </Title>
          <HistoricalDataImportWithStateAndControls
            buttonContainer={containerNode}
            onSkip={handleSkipHistoricalDateImport}
            onSubmit={handleSubmitHistoricalDateImportDates}
          />
        </div>
      )}
      {step === STEPS.SUMMARY && (
        <SummaryStep
          showImportRunningMessage={showImportRunningMessage}
          systemName={systemName}
        />
      )}
      <ModalFooter ref={containerRef}>
        {step === STEPS.CONFIGURATION_FORM && (
          <Button
            color="secondary"
            form={SUB_METERING_FORM_ID}
            loading={createSubMeteringConfigurationMutation.isPending}
            type="submit"
          >
            Weiter zu Zähler
          </Button>
        )}
        {step === STEPS.SUMMARY && (
          <Button color="secondary" onClick={onToggle}>
            Schließen
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

function SummaryStep({ systemName, showImportRunningMessage }) {
  const displayName = getSystemDisplayNameFromValue(systemName);

  return (
    <div className="summary-step">
      <p>Die Schnittstelle zu {displayName} wurde erfolgreich eingerichtet.</p>
      {showImportRunningMessage && <p>{IMPORT_RUNNING_TEXT}</p>}
    </div>
  );
}

export { NewConfigurationModal, NewConfigurationModalProps };
